import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";

import { UploadOutlined } from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Form,
  Row,
  Col,
  Space,
  App,
  Select,
  DatePicker,
  Upload,
  Typography,
  UploadProps,
  InputNumber,
  Input,
} from "antd";

import dayjs, { Dayjs } from "../../../config/dayjs";
import { claimTypeLabels } from "../../../constant/enum";
import { countDateDays } from "../../../utils/countDateUtils";
import useDeviceType from "../../../utils/deviceType";
import {
  createClaim,
  editClaim,
  downloadClaimFileFromS3,
} from "../api/claimAPI";
import { IClaimRequest, ClaimRequestSchema } from "../type/claim";

const { Text } = Typography;

type TimePeriod = "full" | "halfDay";

interface ClaimFormProps {
  initialData?: IClaimRequest;
  mode?: "create" | "update";
  claimId?: string;
}

export const ClaimForm = ({
  initialData,
  mode = "create",
  claimId,
}: ClaimFormProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message: messageApi } = App.useApp();
  const { isMobile } = useDeviceType();

  // Convert initial dates from string to Dayjs for state and form
  const initialStartDate = initialData?.startDate
    ? dayjs(initialData.startDate)
    : null;
  const initialEndDate = initialData?.endDate
    ? dayjs(initialData.endDate)
    : null;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startTimePeriod, setStartTimePeriod] = useState<TimePeriod>("full");
  const [endTimePeriod, setEndTimePeriod] = useState<TimePeriod>("full");
  const [startDate, setStartDate] = useState<Dayjs | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate);
  const [selectedClaimType, setSelectedClaimType] = useState<string>(
    initialData?.claimType || "normalOT"
  );

  // Initialize file list with existing file URL if available
  const [fileList, setFileList] = useState<any[]>(() => {
    if (initialData?.fileUrl && initialData.fileUrl?.length > 0) {
      // Extract filename from URL
      return initialData.fileUrl.map((file, index) => {
        const decodedUrl = decodeURIComponent(file);
        const fileName = decodedUrl.split("/").pop() || "Existing document";
        return {
          uid: `existing-${index}`,
          name: fileName,
          status: "done",
          isExisting: true, // Mark as existing file from server
          s3Path: file, // Store S3 path for reference
        };
      });
    }
    return [];
  });

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IClaimRequest>({
    resolver: zodResolver(ClaimRequestSchema),
    defaultValues: {
      claimType: initialData?.claimType || "normalOT",
      startDate: initialStartDate?.toDate() || undefined,
      endDate: initialEndDate?.toDate() || undefined,
      claimAmount: initialData?.claimAmount || undefined,
      claimDetails: initialData?.claimDetails || undefined,
      totalDays: initialData?.totalDays || undefined,
    },
  });

  // Calculate days required using useMemo for better performance
  const daysRequired = useMemo(() => {
    const includeWeekends =
      selectedClaimType === "weekendOT" || selectedClaimType === "attendClient";

    return countDateDays(
      startDate,
      endDate,
      startTimePeriod,
      endTimePeriod,
      includeWeekends
    );
  }, [startDate, endDate, startTimePeriod, endTimePeriod, selectedClaimType]);

  // Disable weekends (Saturday=6, Sunday=0) for Normal OT
  const disableWeekends = (current: Dayjs) => {
    const day = current.day();
    return day === 0 || day === 6; // Sunday or Saturday
  };

  // Disable weekdays (Monday-Friday) for Weekend OT - only allow Sat/Sun
  const disableWeekdays = (current: Dayjs) => {
    const day = current.day();
    return day !== 0 && day !== 6; // Not Sunday and not Saturday
  };

  const props: UploadProps = {
    name: "file",
    multiple: true,
    fileList: fileList,
    onPreview: async (file: any) => {
      if (file.isExisting && file.s3Path && claimId) {
        try {
          await downloadClaimFileFromS3(claimId, file.s3Path);
        } catch (error) {
          messageApi.error("Failed to open the file");
        }
      } else if (file.url && !file.isExisting) {
        window.open(file.url);
      }
    },
    showUploadList: {
      showPreviewIcon: false,
      showRemoveIcon: true,
    },
    beforeUpload: (file) => {
      // limit file size to 50MB
      if (file.size > 50 * 1024 * 1024) {
        messageApi.error("File must be smaller than 50MB!");
        return Upload.LIST_IGNORE;
      }

      // Add the new uploaded file to the array
      setUploadedFiles((prevFiles) => [...prevFiles, file]);

      // Add the new file to the file list
      setFileList((prevList) => [
        ...prevList,
        {
          uid: file.uid || String(Date.now()),
          name: file.name,
          status: "done",
          url: URL.createObjectURL(file),
          isExisting: false, // Mark as new file
        },
      ]);

      return false; // Prevent automatic upload
    },
    onRemove: (file: any) => {
      // If it's a newly uploaded file (not existing), remove from uploadedFiles array
      if (!file.isExisting) {
        setUploadedFiles((prevFiles) =>
          prevFiles.filter((f) => f.name !== file.name)
        );
      }
      // Remove from fileList (both existing and new files)
      setFileList((prevList) => prevList.filter((f) => f.uid !== file.uid));
    },
    onChange: ({ fileList: newFileList }) => {
      // Update file list when files change
      setFileList(newFileList);
    },
    accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
  };

  const onSubmit = async (data: IClaimRequest) => {
    // Validate dates based on claim type
    if (
      selectedClaimType === "normalOT" ||
      selectedClaimType === "weekendOT" ||
      selectedClaimType === "attendClient"
    ) {
      if (!startDate || !endDate) {
        messageApi.error("Please select date and time");
        return;
      }
      if (endDate.isBefore(startDate) || endDate.isSame(startDate)) {
        messageApi.error("End time must be after start time");
        return;
      }
    } else if (selectedClaimType === "travel" && daysRequired < 0.5) {
      messageApi.error("Please select valid dates for your claim");
      return;
    }

    setIsSubmitting(true);
    try {
      // Only process dates if they exist (for OT claim types)
      // Send as ISO strings to preserve Malaysia timezone
      let startDateToUse: string | undefined;
      let endDateToUse: string | undefined;

      if (startDate && endDate) {
        // For Normal OT and Weekend OT - use the exact time selected
        // For Travel - set time based on half/full day selection
        if (selectedClaimType === "travel") {
          // Set time based on period selection for travel claims
          let adjustedStartDate = startDate;
          let adjustedEndDate = endDate;

          if (startTimePeriod === "halfDay") {
            adjustedStartDate = startDate
              .hour(8)
              .minute(0)
              .second(0)
              .millisecond(0);
          } else {
            adjustedStartDate = startDate
              .hour(8)
              .minute(0)
              .second(0)
              .millisecond(0); // Full day starts at 8am
          }

          if (endTimePeriod === "halfDay") {
            adjustedEndDate = endDate
              .hour(12)
              .minute(0)
              .second(0)
              .millisecond(0); // Half day ends at noon
          } else {
            adjustedEndDate = endDate
              .hour(18)
              .minute(0)
              .second(0)
              .millisecond(0); // Full day ends at 6pm
          }

          // Format as ISO 8601 with timezone offset to preserve Malaysia timezone
          startDateToUse = adjustedStartDate.format();
          endDateToUse = adjustedEndDate.format();
        } else {
          // For OT claims, format with timezone offset to preserve Malaysia timezone
          // This sends the date as "2025-01-28T07:00:00+08:00" instead of UTC
          startDateToUse = startDate.format();
          endDateToUse = endDate.format();
        }
      }

      const submitData: any = {
        ...data,
        ...(startDateToUse && { startDate: startDateToUse }),
        ...(endDateToUse && { endDate: endDateToUse }),
        ...(daysRequired > 0 && { totalDays: daysRequired }),
      };

      if (mode === "create") {
        await createClaim(
          submitData,
          uploadedFiles.length > 0 ? uploadedFiles : undefined
        );
        messageApi.success("Claim application submitted successfully!");
      } else if (claimId) {
        // Get existing files that weren't removed
        const existingFileUrls = fileList
          .filter((file: any) => file.isExisting)
          .map((file: any) => file.s3Path || file.url);

        await editClaim(
          claimId,
          submitData,
          uploadedFiles.length > 0 ? uploadedFiles : undefined,
          existingFileUrls.length > 0 ? existingFileUrls : undefined
        );
        messageApi.success("Claim application updated successfully!");

        // Refresh data
        queryClient.invalidateQueries({ queryKey: ["claim", claimId] });
      }
      navigate(-1);
    } catch (error: any) {
      messageApi.error(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Form
      layout="vertical"
      onFinish={handleSubmit(onSubmit, () => {
        messageApi.error("Please check all required fields");
      })}
    >
      <Row gutter={[24, 16]}>
        {/* ================ Claim Type Field ================ */}
        <Col xs={24}>
          <Form.Item
            label="Claim Type"
            validateStatus={errors.claimType ? "error" : ""}
            help={errors.claimType?.message}
            style={{ marginBottom: 24 }}
            required
          >
            <Controller
              name="claimType"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Select Claim Type"
                  {...field}
                  size="large"
                  style={{ width: "100%" }}
                  onChange={(value) => {
                    field.onChange(value);
                    setSelectedClaimType(value);
                  }}
                  options={[
                    { value: "normalOT", label: claimTypeLabels.normalOT },
                    { value: "weekendOT", label: claimTypeLabels.weekendOT },
                    {
                      value: "attendClient",
                      label: claimTypeLabels.attendClient,
                    },
                    { value: "medical", label: claimTypeLabels.medical },
                    { value: "travel", label: claimTypeLabels.travel },
                    { value: "commission", label: claimTypeLabels.commission },
                  ]}
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Select Date & Time Section for Normal OT, Weekend OT & Attend Client ================ */}
        {(selectedClaimType === "normalOT" ||
          selectedClaimType === "weekendOT" ||
          selectedClaimType === "attendClient") && (
          <Col xs={24}>
            <Text type="danger">* </Text>
            <Text style={{ fontSize: 16 }}>
              {selectedClaimType === "normalOT"
                ? "Select date & time (weekdays only)"
                : selectedClaimType === "weekendOT"
                ? "Select date & time (weekends only)"
                : "Select date & time (any day)"}
            </Text>
            <Row
              gutter={16}
              style={{ marginTop: 12, marginBottom: 24 }}
              align="middle"
            >
              {/* Date */}
              <Col xs={24} sm={8}>
                <Form.Item
                  validateStatus={errors.startDate ? "error" : ""}
                  help={errors.startDate?.message}
                  style={{ marginBottom: isMobile ? 5 : 0 }}
                >
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        placeholder="Select Date"
                        {...field}
                        value={startDate}
                        onChange={(date) => {
                          field.onChange(date);
                          setStartDate(date);
                          // Also update endDate to same day if exists
                          if (date && endDate) {
                            const newEndDate = date
                              .clone()
                              .hour(endDate.hour())
                              .minute(endDate.minute());
                            setEndDate(newEndDate);
                          } else if (date) {
                            setEndDate(date.clone());
                          }
                        }}
                        disabledDate={
                          selectedClaimType === "normalOT"
                            ? disableWeekends
                            : selectedClaimType === "weekendOT"
                            ? disableWeekdays
                            : undefined
                        }
                        size="large"
                        format="DD/MM/YYYY"
                        style={{
                          width: "100%",
                          borderColor: errors.startDate ? "#ff4d4f" : "#a6a6a6",
                        }}
                      />
                    )}
                  />
                </Form.Item>
              </Col>

              {/* Start Time */}
              <Col xs={24} sm={6}>
                <Form.Item
                  style={{ marginBottom: isMobile ? 5 : 0 }}
                  help={
                    errors.startDate || errors.endDate ? (
                      <span style={{ visibility: "hidden" }}>placeholder</span>
                    ) : undefined
                  }
                >
                  <DatePicker
                    picker="time"
                    placeholder="Start Time"
                    value={startDate}
                    onChange={(time) => {
                      if (time && startDate) {
                        const newStartDate = startDate
                          .clone()
                          .hour(time.hour())
                          .minute(time.minute());
                        setStartDate(newStartDate);
                      } else if (time) {
                        setStartDate(time);
                      }
                    }}
                    format="HH:mm"
                    size="large"
                    style={{
                      width: "100%",
                      borderColor: errors.startDate ? "#ff4d4f" : "#a6a6a6",
                    }}
                    disabled={!startDate}
                  />
                </Form.Item>
              </Col>

              {/* "to" text */}
              <Col xs={24} sm={2} style={{ textAlign: "center" }}>
                <Form.Item
                  style={{ marginBottom: isMobile ? 5 : 0 }}
                  help={
                    errors.startDate || errors.endDate ? (
                      <span style={{ visibility: "hidden" }}>placeholder</span>
                    ) : undefined
                  }
                >
                  <Text strong style={{ fontSize: 16 }}>
                    to
                  </Text>
                </Form.Item>
              </Col>

              {/* End Time */}
              <Col xs={24} sm={6}>
                <Form.Item
                  validateStatus={errors.endDate ? "error" : ""}
                  help={
                    errors.endDate?.message ||
                    (errors.startDate ? (
                      <span style={{ visibility: "hidden" }}>placeholder</span>
                    ) : undefined)
                  }
                  style={{ marginBottom: isMobile ? 5 : 0 }}
                >
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        picker="time"
                        placeholder="End Time"
                        {...field}
                        value={endDate}
                        onChange={(time) => {
                          if (time && startDate) {
                            // Use start date's date part with selected time
                            const newEndDate = startDate
                              .clone()
                              .hour(time.hour())
                              .minute(time.minute());
                            field.onChange(newEndDate);
                            setEndDate(newEndDate);
                          }
                        }}
                        format="HH:mm"
                        size="large"
                        style={{
                          width: "100%",
                          borderColor: errors.endDate ? "#ff4d4f" : "#a6a6a6",
                        }}
                        disabled={!startDate}
                      />
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        )}

        {/* ================ Select Dates Section for Travel ================ */}
        {selectedClaimType === "travel" && (
          <Col xs={24}>
            <Text type="danger">* </Text>
            <Text style={{ fontSize: 16 }}>Select dates</Text>
            <Row
              gutter={16}
              style={{ marginTop: 12, marginBottom: 24 }}
              align="middle"
            >
              {/* Start Date */}
              <Col xs={24} sm={11}>
                <Row gutter={8}>
                  <Col span={14}>
                    <Form.Item
                      validateStatus={errors.startDate ? "error" : ""}
                      help={errors.startDate?.message}
                      style={{ marginBottom: 0 }}
                    >
                      <Controller
                        name="startDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            placeholder="Select Start Date"
                            {...field}
                            value={startDate}
                            onChange={(date) => {
                              field.onChange(date);
                              setStartDate(date);
                            }}
                            size="large"
                            format="DD/MM/YYYY"
                            style={{
                              width: "100%",
                              borderColor: errors.startDate
                                ? "#ff4d4f"
                                : "#a6a6a6",
                            }}
                          />
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Select
                      value={startTimePeriod}
                      onChange={setStartTimePeriod}
                      size="large"
                      style={{ width: "100%" }}
                      options={[
                        { value: "full", label: "full day" },
                        { value: "halfDay", label: "half day" },
                      ]}
                      suffixIcon={<span style={{ fontSize: 12 }}>▼</span>}
                    />
                  </Col>
                </Row>
              </Col>

              {/* "to" text */}
              <Col xs={24} sm={2} style={{ textAlign: "center" }}>
                <Text strong style={{ fontSize: 16 }}>
                  to
                </Text>
              </Col>

              {/* End Date */}
              <Col xs={24} sm={11}>
                <Row gutter={8}>
                  <Col span={14}>
                    <Form.Item
                      validateStatus={errors.endDate ? "error" : ""}
                      help={errors.endDate?.message}
                      style={{ marginBottom: 0 }}
                    >
                      <Controller
                        name="endDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            placeholder="Select End Date"
                            {...field}
                            value={endDate}
                            onChange={(date) => {
                              field.onChange(date);
                              setEndDate(date);
                            }}
                            size="large"
                            format="DD/MM/YYYY"
                            style={{
                              width: "100%",
                              borderColor: errors.endDate
                                ? "#ff4d4f"
                                : "#a6a6a6",
                            }}
                          />
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Select
                      value={endTimePeriod}
                      onChange={setEndTimePeriod}
                      size="large"
                      style={{ width: "100%" }}
                      options={[
                        { value: "full", label: "full day" },
                        { value: "halfDay", label: "half day" },
                      ]}
                      suffixIcon={<span style={{ fontSize: 12 }}>▼</span>}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        )}

        {/* ================ Claim Amount Field ================ */}
        {selectedClaimType !== "normalOT" &&
          selectedClaimType !== "weekendOT" &&
          selectedClaimType !== "attendClient" && (
            <Col xs={24}>
              <Form.Item
                label="Claim Amount"
                name="claimAmount"
                validateStatus={errors.claimAmount ? "error" : ""}
                help={errors.claimAmount?.message}
                required={
                  selectedClaimType === "medical" ||
                  selectedClaimType === "travel" ||
                  selectedClaimType === "commission"
                }
              >
                <Controller
                  name="claimAmount"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      min={0}
                      step={0.01}
                      placeholder="Enter amount"
                      {...field}
                      size="large"
                      style={{
                        width: "100%",
                        borderColor: errors.claimAmount ? "#ff4d4f" : "#A6A6A6",
                      }}
                      formatter={(value) =>
                        value
                          ? value
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                          : ""
                      }
                    />
                  )}
                />
              </Form.Item>
            </Col>
          )}

        {/* ================ Claim Details Field ================ */}
        {selectedClaimType === "travel" && (
          <Col xs={24}>
            <Form.Item
              label="Claim Details"
              name="claimDetails"
              hasFeedback
              validateStatus={errors.claimDetails ? "error" : ""}
              help={errors.claimDetails?.message}
            >
              <Controller
                name="claimDetails"
                control={control}
                render={({ field }) => (
                  <Input.TextArea
                    placeholder="Enter claim details"
                    {...field}
                    size="large"
                    rows={4}
                  />
                )}
              />
            </Form.Item>
          </Col>
        )}

        {/* ================ Upload File Field ================ */}
        {(selectedClaimType === "medical" ||
          selectedClaimType === "travel") && (
          <Col xs={24}>
            <Form.Item
              label="Upload Document"
              name="fileUpload"
              tooltip="Allowed file types: PDF, DOC, DOCX, JPG, JPEG, PNG"
            >
              <div className="custom-upload-list">
                <Upload {...props}>
                  <Button
                    icon={<UploadOutlined />}
                    size="large"
                    style={{
                      backgroundColor: "#5b8db8",
                      color: "white",
                      border: "none",
                      borderRadius: 4,
                      fontWeight: 500,
                    }}
                  >
                    Select Files
                  </Button>
                </Upload>
              </div>
            </Form.Item>
          </Col>
        )}
      </Row>

      {/* ================ Form Actions ================ */}
      <Form.Item>
        <Row justify="end">
          <Space
            direction="horizontal"
            wrap
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              style={{ margin: 5 }}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              style={{ margin: 5 }}
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
            >
              {mode === "create" ? "Submit Claim" : "Update Claim"}
            </Button>
          </Space>
        </Row>
      </Form.Item>
    </Form>
  );
};
